import { useEffect, useRef, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";

import Footer from "@components/dashboard/Footer";
import Loader from "@components/Loader";
import Sidebar from "@components/dashboard/Sidebar";

import ProjectProvider, { useProjectActions, useProjectContext } from "@contexts/projects";
import {
  useDashboardUIActions,
  useDashboardUIContext,
} from "@contexts/dashboardUI";
import ItemDetailOffcanvas from "@components/dashboard/ItemDetailOffcanvas";
import Offcanvas from "@components/dashboard/Offcanvas";
import Header from "@components/dashboard/Header";
import HelpModal from "@components/HelpModal";
import Modal from "@components/Modal";
import { useAuthActions, useAuthContext } from "@contexts/auth";
import { trackEvent } from "@utils/activityTracker";
import { isProjectScopedPath, projectIdFromPath, projectListOptions } from "./dashboardNavigation";

const Dashboard = () => {
  const { isLoading, showSidebar, sidebarTriggerKey } = useDashboardUIContext();
  const { projects, activeProject } = useProjectContext();
  const { setIsLoading, openSidebar } = useDashboardUIActions();
  const { list: listProjects, selectProject } = useProjectActions();
  const { user } = useAuthContext()
  const { updatePassword } = useAuthActions();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const projectScoped = isProjectScopedPath(pathname);
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    newPasswordConfirmation: ''
  })
  const sidebarWasOpen = useRef(showSidebar);
  const lastTrackedProject = useRef(null);

  useEffect(() => {
    (async () => {

      const projectId = projectIdFromPath(pathname);
      setIsLoading(true);
      try {
        await listProjects(
          projectId,
          { id: user?._id, token: user?.token },
          projectListOptions(pathname),
        );
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!projects?.length) return;
    // if (paths.filter(i => pathname.includes(i)).length !== 0) return;

    if (!isProjectScopedPath(pathname)) return;
    const projectId = projectIdFromPath(pathname);
    // const projectId = '66eacb4976d7c7909b0e9a91' //HARDCODED
    //essa gambiarra ta aqui por conta de como foi estruturado o inicio do projeto, e agora vai ficar assim
    if (!projects.find(({ _id }) => _id === projectId)) {
      const fallbackId = activeProject?._id ?? projects[0]?._id;
      if (fallbackId) navigate(`${pathname}/${fallbackId}`)
    }
  }, [projects, pathname, activeProject])

  useEffect(() => {
    if (!isProjectScopedPath(pathname)) return;
    const projectId = projectIdFromPath(pathname);
    const project = projects?.find(({ _id }) => _id === projectId);
    if (project && activeProject?._id != projectId) {
      selectProject(projectId)
      // selectProject é assíncrono: o efeito pode rodar de novo antes de activeProject mudar.
      if (lastTrackedProject.current === projectId) return;
      lastTrackedProject.current = projectId;
      trackEvent("project_selected", {
        projectId,
        metadata: {
          buildingName: project.building_name,
          companyId: project.company_id,
          previousProjectId: activeProject?._id,
        },
      });
    }
  }, [pathname, projects, activeProject])

  useEffect(() => {
    const wasOpen = sidebarWasOpen.current;
    sidebarWasOpen.current = showSidebar;
    if (!wasOpen || showSidebar || !sidebarTriggerKey) return;
    document.querySelector(
      `[data-dashboard-menu-trigger="${sidebarTriggerKey}"]`,
    )?.focus();
  }, [showSidebar, sidebarTriggerKey, pathname]);

  const handleInput = (e) => {
    setPasswordForm(prev => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleSend = async (e) => {
    e.preventDefault()
    if (passwordForm.newPassword !== passwordForm.newPasswordConfirmation) {
      alert('As senhas não conferem. Por favor, tente novamente.')
      return;
    }
    const response = await updatePassword({ ...passwordForm, email: user.email })
    if (response.ok) {
      window.location.reload()
    }
  }


  return (
    // <section id="dashboard" className="dark-theme">
    <section id="dashboard" >
      {isLoading && <Loader />}

      {(user && !user.changedPassword) && <Modal>
        {/* Abre com qualquer senha recebida por e-mail (conta nova, senha de acesso único ou reset
            pelo administrador): o título é o que os e-mails de conta citam no passo a passo. */}
        <h1>Defina sua nova senha</h1>
        <span>Você entrou com uma senha enviada por e-mail. Por segurança, cadastre uma senha pessoal para continuar.</span>
        <p>Nova senha</p>
        <input className="resource-search" type="password" name='newPassword' onChange={handleInput} value={passwordForm.newPassword} />
        <p>Confirme sua nova senha</p>
        <input className="resource-search" type="password" name='newPasswordConfirmation' onChange={handleInput} value={passwordForm.newPasswordConfirmation} />
        <br />
        <button className="button button-accent margin-top-12" onClick={handleSend}>Enviar</button>
      </Modal>}

      {projectScoped && <Header />}

      {!projectScoped && !showSidebar && (
        <button
          type="button"
          className="toggle-menu-button dashboard-global-menu-toggle"
          aria-label="Abrir menu de navegação"
          aria-controls="dashboard-sidebar"
          aria-expanded="false"
          data-dashboard-menu-trigger="global"
          onClick={() => openSidebar("global")}
        >
          <i
            className="icon icon-width-24 icon-angle-double-right background-accent"
            aria-hidden="true"
          />
        </button>
      )}

      <Sidebar />

      <main className={showSidebar ? 'padding-left' : ''}>
        <Outlet />
      </main>

      <Footer />

      <HelpModal />

      <Offcanvas />
      <ItemDetailOffcanvas />

    </section >
  );
};

export default Dashboard;
